const sortSubmissionsByCreatedOn = (submissions = []) => {
    return [...submissions]
        .filter((submission) => submission?.created_on)
        .sort((left, right) => (left.created_on || 0) - (right.created_on || 0));
};

const sortChatMessagesByCreatedOn = (messages = []) => {
    return [...messages].sort((left, right) => {
        const leftCreatedOn = left?.created_on || 0;
        const rightCreatedOn = right?.created_on || 0;

        if (leftCreatedOn !== rightCreatedOn) {
            return leftCreatedOn - rightCreatedOn;
        }

        return (left?.modified_on || leftCreatedOn) - (right?.modified_on || rightCreatedOn);
    });
};

const buildSubmissionCreatedSystemMessage = (submission, index) => {
    return {
        id: `submission-created-${submission.id}`,
        active: true,
        role: 'system',
        created_on: submission.created_on,
        modified_on: submission.created_on,
        message_text: `Draft ${index + 1} has been submitted at`,
        message_context: null,
        system_type: 'submission_created',
        system_data: {
            draftNumber: index + 1,
        },
    };
};

const getSubmissionCreatedDraftNumber = (message) => {
    if (!message || message.role !== 'system') return null;

    if (message.system_type === 'submission_created') {
        return message.system_data?.draftNumber ?? null;
    }

    const text = `${message.message_text || ''}`;
    const match = text.match(/^Draft\s+(\d+)\s+has been submitted at$/i);
    if (!match) return null;

    return Number(match[1]);
};

const hasSubmissionCreatedSystemMessage = ({ chat, draftNumber, messageId }) => {
    return (chat?.messages || []).some((message) => {
        if (message?.id === messageId) return true;
        return getSubmissionCreatedDraftNumber(message) === draftNumber;
    });
};

const buildStudentFeedbackChatViewModel = () => {
    return {
        header: {
            title: 'Welcome',
            infoTooltip:
                'This feature is here to help you inquire further. Please note that this chat is visible to your instructor.',
        },
    };
};

const getLatestMessageByRole = ({ chat, role, systemType }) => {
    const messages = sortChatMessagesByCreatedOn(chat?.messages || []);

    return (
        [...messages].reverse().find((message) => {
            if (message?.role !== role) return false;
            if (!systemType) return true;
            return message?.system_type === systemType;
        }) || null
    );
};

const getLatestChatMessage = (chat) => {
    const messages = sortChatMessagesByCreatedOn(chat?.messages || []);
    if (messages.length > 0) {
        return [...messages].reverse().find((message) => message?.role !== 'system') || null;
    }

    if (chat?.latest_message) {
        return chat.latest_message;
    }

    return null;
};

const getLatestSubmissionCreatedMessage = (chat) => {
    return getLatestMessageByRole({
        chat,
        role: 'system',
        systemType: 'submission_created',
    });
};

const shouldShowChatSuggestions = ({ chat }) => {
    const latestChatMessage = getLatestMessageByRole({ chat, role: 'user' });
    const latestSubmissionCreatedMessage = getLatestSubmissionCreatedMessage(chat);

    if (!latestSubmissionCreatedMessage) {
        return !latestChatMessage;
    }

    if (!latestChatMessage) {
        return true;
    }

    return latestSubmissionCreatedMessage.created_on > latestChatMessage.created_on;
};

const hasUnreadChat = ({ chat, accountId }) => {
    if (!chat || !accountId) return false;

    const latestMessage = getLatestChatMessage(chat);
    if (!latestMessage) return false;
    if (latestMessage.role === 'user') return false;

    const readEntry = (chat.read_state || []).find((item) => item.account_id === accountId);
    return readEntry?.last_read_message_id !== latestMessage.id;
};

export {
    buildStudentFeedbackChatViewModel,
    buildSubmissionCreatedSystemMessage,
    getLatestChatMessage,
    getSubmissionCreatedDraftNumber,
    hasSubmissionCreatedSystemMessage,
    hasUnreadChat,
    shouldShowChatSuggestions,
};
